import { Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { normalizeSearch } from "../lib/content";
import type { Heading, SourcesData } from "../types/content";

type SearchDialogProps = {
  open: boolean;
  onClose: () => void;
  headings: Heading[];
  sources: SourcesData;
};

export function SearchDialog({ open, onClose, headings, sources }: SearchDialogProps) {
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const normalized = normalizeSearch(query);
  const results = useMemo(() => {
    if (!normalized) return [];
    const headingResults = headings
      .filter((item) => normalizeSearch(item.text).includes(normalized))
      .slice(0, 8)
      .map((item) => ({ label: item.text, href: `#${item.id}`, type: "章节" }));
    const sourceResults = sources.articles
      .filter((item) => normalizeSearch(item.title).includes(normalized))
      .slice(0, 6)
      .map((item) => ({
        label: item.title,
        href: `#source-${item.id.toLowerCase()}`,
        type: "来源",
      }));
    return [...headingResults, ...sourceResults];
  }, [headings, normalized, sources.articles]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    window.setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  if (!open) return null;

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="search-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="搜索文档"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <label>
          <Search aria-hidden="true" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="搜索章节、术语或来源…"
          />
          <button type="button" onClick={onClose} aria-label="关闭搜索">
            <X aria-hidden="true" />
          </button>
        </label>
        <div className="search-results">
          {!query && <p>试试搜索 KV Cache、TTFT、vLLM 或量化。</p>}
          {query && !results.length && <p>没有匹配项。</p>}
          {results.map((result) => (
            <a key={`${result.type}-${result.href}`} href={result.href} onClick={onClose}>
              <span>{result.type}</span>
              {result.label}
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
